import React from 'react';
import { getIn, useFormikContext } from 'formik';

import { useStyles } from '~/assets/styles';
import ListItemText from '~/components/core/Atomic/ListItemText';
import MenuItem from '~/components/core/Atomic/MenuItem';
import SingleSelectField from '~/components/core/Molecules/Fields/SingleSelectField';
import Checkbox from '~/components/ui/atoms/Checkbox';
import executeOrDelegate from '~/Utils/executeOrDelegate';

import { ErrorHelperTextFormik } from '../../core/Formik/ErrorHelperTextFormik';
import useOrganization from '../../OrganizationContext';

const ALL_ITEM_VALUE = '__all__';

interface PartialValuesType {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [name: string]: any;
}

interface SubOrganizationMultiselectFormikProps {
  subOrganizationsFieldId: string;
  allSelectedFieldId?: string;
  label?: string;
  selectAllLabel?: string;
  showAllInRenderedSelectedOptions?: boolean;
  shouldDisplayAllOption?: boolean;
  onChange?: (p: { subOrgs: string | string[]; selectAll: boolean }) => void;
  disabled?: boolean;
  specificSubOrgIdOptions?: number[];
  /**
   * Never leave the selection empty: unticking All with nothing else picked is a no-op, and removing the
   * last picked sub-org snaps the selection back to All. Opt-in because an empty selection is meaningful
   * on the other screens (an unset search filter, or a state their own validation blocks). Only applies
   * where the All row is actually displayed — without it, an All state would render as nothing selected.
   */
  emptySelectionFallsBackToAll?: boolean;
  handleChangeWrapper?: (changeFunc: () => void) => void; // accepts a change func that can be called or not after performing other logic
}

const SubOrganizationMultiselectFormik: React.FC<SubOrganizationMultiselectFormikProps> = ({
  subOrganizationsFieldId,
  allSelectedFieldId,
  onChange,
  label = 'Sub-Organizations',
  selectAllLabel = 'All',
  showAllInRenderedSelectedOptions = false,
  shouldDisplayAllOption = false,
  disabled = false,
  specificSubOrgIdOptions,
  emptySelectionFallsBackToAll = false,
  handleChangeWrapper,
}) => {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const { subOrganizationEnabled, subOrganizations } = useOrganization();
  const { setFieldValue, values, errors, touched } = useFormikContext<PartialValuesType>();
  const { classes } = useStyles();

  if (!subOrganizationEnabled) {
    return null;
  }
  const subOrganizationOptions = specificSubOrgIdOptions
    ? subOrganizations.filter((subOrg: { id: number; name: string }) => {
        if (specificSubOrgIdOptions) {
          return specificSubOrgIdOptions.includes(subOrg.id);
        }
        return true;
      })
    : subOrganizations;
  if (shouldDisplayAllOption && subOrganizationOptions.length === 1) {
    shouldDisplayAllOption = false;
  }

  const selectedSubOrgs = getIn(values, subOrganizationsFieldId) || [];
  const isSelectAllChecked =
    shouldDisplayAllOption &&
    ((allSelectedFieldId && values[allSelectedFieldId]) || // selectAllField was passed in, get value
      (!allSelectedFieldId && selectedSubOrgs.length === 0) || // selectAllField was not passed in, is list empty
      false);

  const callOnChange = (subOrgs: string | string[], selectAll: boolean) => {
    if (onChange) {
      onChange({ subOrgs, selectAll });
    }
  };

  const setAllSelectedFieldValue = (value: boolean) => {
    if (allSelectedFieldId) {
      setFieldValue(allSelectedFieldId, value);
    }
  };

  const handleSelectionChange = (value: string | string[]) => {
    if (!value?.includes(ALL_ITEM_VALUE)) {
      if (emptySelectionFallsBackToAll && shouldDisplayAllOption && value?.length === 0) {
        executeOrDelegate({
          primaryTask: () => {
            setFieldValue(subOrganizationsFieldId, []);
            setAllSelectedFieldValue(true);
            callOnChange([], true);
          },
          wrapperFunction: handleChangeWrapper,
        });
        return;
      }
      executeOrDelegate({
        primaryTask: () => {
          setFieldValue(subOrganizationsFieldId, value);
          setAllSelectedFieldValue(false);
          callOnChange(value, false);
        },
        wrapperFunction: handleChangeWrapper,
      });
    }
  };

  const handleSelectAll = (event: { stopPropagation: () => void }) => {
    event.stopPropagation();
    if (emptySelectionFallsBackToAll && isSelectAllChecked && selectedSubOrgs.length === 0) {
      // Unticking All with nothing else picked would leave an empty selection — keep All.
      return;
    }
    const newSelectAllValue = !isSelectAllChecked;

    executeOrDelegate({
      primaryTask: () => {
        setAllSelectedFieldValue(newSelectAllValue);

        if (newSelectAllValue) {
          setFieldValue(subOrganizationsFieldId, []);
        }

        callOnChange(newSelectAllValue ? [] : selectedSubOrgs, newSelectAllValue);
      },
      wrapperFunction: handleChangeWrapper,
    });
  };

  const isSubOrgChecked = (id: number) => {
    return getIn(values, subOrganizationsFieldId).indexOf(id) > -1;
  };

  const renderFieldValue = (ids: unknown) => {
    if (showAllInRenderedSelectedOptions && isSelectAllChecked) {
      return selectAllLabel;
    }
    return (ids as number[])
      .map((id) => subOrganizationOptions.find((subOrg: { id: number }) => id === subOrg.id)?.name)
      .join(', ');
  };

  return (
    <>
      <SingleSelectField
        id={subOrganizationsFieldId}
        label={label}
        className={classes.textField}
        value={selectedSubOrgs}
        onChange={handleSelectionChange}
        disabled={disabled}
        error={
          (getIn(errors, subOrganizationsFieldId) && getIn(touched, subOrganizationsFieldId)) ||
          (allSelectedFieldId && getIn(errors, allSelectedFieldId) && getIn(touched, allSelectedFieldId))
        }
        fullWidth
        InputLabelProps={{
          shrink: (showAllInRenderedSelectedOptions && isSelectAllChecked) || !!selectedSubOrgs.length,
        }}
        SelectProps={{
          multiple: true,
          renderValue: renderFieldValue,
          displayEmpty: true,
        }}
      >
        {shouldDisplayAllOption ? (
          <MenuItem value={ALL_ITEM_VALUE}>
            <Checkbox checked={isSelectAllChecked} onChange={handleSelectAll} />
            <ListItemText primary={selectAllLabel} onClick={handleSelectAll} />
          </MenuItem>
        ) : null}
        {subOrganizationOptions.map((subOrg: { id: number; name: string }) => (
          <MenuItem key={subOrg.id} value={subOrg.id}>
            <Checkbox checked={isSubOrgChecked(subOrg.id)} />
            <ListItemText primary={subOrg.name} />
          </MenuItem>
        ))}
      </SingleSelectField>
      {allSelectedFieldId && <ErrorHelperTextFormik id={allSelectedFieldId} />}
      <ErrorHelperTextFormik id={subOrganizationsFieldId} />
    </>
  );
};

export default SubOrganizationMultiselectFormik;
